// src/components/dashboard/PickUpWhereYouLeftOff.jsx
//
// The dashboard's first card: where you were. The module you were part-way
// through (and the part), or the next one you haven't finished. One tap opens
// it, and the module itself scrolls you back to where you stopped.
//
// Replaces "Your Next Step", which priced each step in XP. Nothing here is
// counted, and nothing is lost by being away.
import React, { useMemo } from 'react';
import { BookOpen, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../shared/CardComponents';
import Button from '../shared/Button';
import { friendshipModules } from '../../data/modules/friendship-modules-index';
import { readCompletedParts, readModulePosition } from '../../utils/modulePartProgress';
import { findResumePoint } from '../../utils/dashboardPlan';

const openModule = (moduleId, setCurrentView) => {
  // The modules page opens the module named here.
  try { localStorage.setItem('returnToModule', String(moduleId)); } catch { /* opens the list instead */ }
  setCurrentView('modules');
};

const PickUpWhereYouLeftOff = ({ completedModules = [], setCurrentView }) => {
  const resume = useMemo(
    () => findResumePoint({
      modules: friendshipModules,
      completedModules,
      readParts: readCompletedParts,
      readPosition: readModulePosition,
    }),
    [completedModules],
  );

  return (
    <Card className="bg-gradient-to-br from-teal-500/10 to-purple-500/10 border-teal-500/30" data-testid="pick-up-where-you-left-off">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-white text-lg">
          <BookOpen className="w-5 h-5 text-teal-400" aria-hidden="true" />
          Pick up where you left off
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {resume.kind === 'continue' && (
          <>
            <div>
              <p className="text-sm text-teal-300">Module {resume.module.id}</p>
              <h3 className="text-xl font-bold text-white leading-snug">{resume.module.title}</h3>
              {resume.part && (
                <p className="text-slate-300 mt-1">
                  Part {resume.part.number} of {resume.partsTotal}: {resume.part.title}
                </p>
              )}
            </div>
            <Button
              onClick={() => openModule(resume.module.id, setCurrentView)}
              className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white"
            >
              Carry on reading
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </Button>
          </>
        )}

        {resume.kind === 'start' && (
          <>
            <div>
              <p className="text-sm text-teal-300">Module {resume.module.id}</p>
              <h3 className="text-xl font-bold text-white leading-snug">{resume.module.title}</h3>
              <p className="text-slate-300 mt-1">
                {completedModules.length === 0
                  ? 'A good place to start. The modules can be read in any order.'
                  : 'The next module you haven’t read yet. Any of the others is fine too.'}
              </p>
            </div>
            <Button
              onClick={() => openModule(resume.module.id, setCurrentView)}
              className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white"
            >
              Start reading
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </Button>
          </>
        )}

        {resume.kind === 'done' && (
          <>
            <p className="text-slate-300 flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
              You&apos;ve read all eight modules. Any of them is worth a second read when something in it comes up for real.
            </p>
            <Button
              onClick={() => setCurrentView('modules')}
              className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white"
            >
              Open the modules
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PickUpWhereYouLeftOff;
